import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  changeProgramDateBasis,
  changeProgramUnit,
  createCommercialProgram,
  updateCommercialProgram,
} from 'common/src/api/commercialProgram';
import {
  PROGRAM_UNITS,
  PROGRAM_UNIT_LABELS,
  PROGRAM_UNIT_SHORT,
  convertQuantity,
  formatProgramQuantity,
} from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  ProgramDateBasis,
  ProgramUnit,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert } from '../../components/ui';

/** Un compromiso vigente, para mostrar cómo quedaría al cambiar de unidad. */
export interface UnitPreviewRow {
  label: string;
  quantity: number;
}

interface ProgramSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Sin programa: se crea. Con programa: se editan sus parámetros. */
  program: CommercialProgram | null;
  exporterId: number;
  season: string;
  speciesId: number;
  defaultKgPerCeq: number;
  userId?: number;
  previewRows?: UnitPreviewRow[];
  onSaved: () => Promise<void> | void;
}

const toNumber = (value: string) => {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

const PREVIEW_ROWS = 3;

/**
 * Parámetros del programa. La unidad y la fecha base no se cambian en el
 * lugar: se guarda una versión con el programa como estaba.
 */
export default function ProgramSettingsModal({
  isOpen,
  onClose,
  program,
  exporterId,
  season,
  speciesId,
  defaultKgPerCeq,
  userId,
  previewRows = [],
  onSaved,
}: ProgramSettingsModalProps) {
  const { t } = useTranslation('commitments');
  const [unit, setUnit] = useState<ProgramUnit>('ceq');
  const [dateBasis, setDateBasis] = useState<ProgramDateBasis>('etd');
  const [kgPerCeq, setKgPerCeq] = useState('');
  const [kgPerShipment, setKgPerShipment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setUnit(program?.unit ?? 'ceq');
    setDateBasis(program?.date_basis ?? 'etd');
    setKgPerCeq(String(program?.kg_per_ceq ?? defaultKgPerCeq));
    setKgPerShipment(
      program?.kg_per_shipment ? String(program.kg_per_shipment) : ''
    );
  }, [isOpen, program, defaultKgPerCeq]);

  const unitChanged = !!program && program.unit !== unit;
  const basisChanged = !!program && program.date_basis !== dateBasis;
  const ceq = toNumber(kgPerCeq);
  const shipmentKg = kgPerShipment.trim() ? toNumber(kgPerShipment) : null;
  const unitsInvolved = [
    unit,
    ...(unitChanged && program ? [program.unit] : []),
  ];
  const needsShipmentKg = unitsInvolved.includes('shipment');
  const needsCeqKg = unitsInvolved.includes('ceq');
  const shipmentKgInvalid =
    (needsShipmentKg && !shipmentKg) ||
    (kgPerShipment.trim() !== '' && !shipmentKg);
  const invalid = !ceq || shipmentKgInvalid;

  // Cómo quedan algunos compromisos con las equivalencias escritas.
  const preview =
    unitChanged && program && ceq && !shipmentKgInvalid
      ? previewRows.slice(0, PREVIEW_ROWS).map((row) => ({
          ...row,
          converted: convertQuantity(row.quantity, program.unit, unit, {
            kg_per_ceq: ceq,
            kg_per_shipment: shipmentKg,
          }),
        }))
      : [];

  const handleSave = async () => {
    if (invalid || !ceq) return;
    setSaving(true);
    try {
      if (!program) {
        await createCommercialProgram({
          exporter_id: exporterId,
          season,
          species_id: speciesId,
          unit,
          date_basis: dateBasis,
          kg_per_ceq: ceq,
          kg_per_shipment: shipmentKg,
          created_by: userId ?? null,
        });
        toast.success(t('Programa creado'));
      } else {
        await updateCommercialProgram(program.id, {
          kg_per_ceq: ceq,
          kg_per_shipment: shipmentKg,
        });
        if (basisChanged) await changeProgramDateBasis(program.id, dateBasis);
        if (unitChanged)
          await changeProgramUnit(program.id, unit, ceq, shipmentKg);
        toast.success(t('Programa actualizado'));
      }
      await onSaved();
      onClose();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(t('No se pudo guardar: {{error}}', { error: message }));
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = (u: ProgramUnit) => t(PROGRAM_UNIT_LABELS[u]).toLowerCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalContent>
        <ModalHeader>
          {program
            ? t('Configuración del programa')
            : t('Crear programa comercial')}
        </ModalHeader>
        <ModalBody className='gap-3'>
          <div className='grid grid-cols-2 gap-3'>
            <Select
              label={t('Unidad')}
              selectedKeys={[unit]}
              onSelectionChange={(keys) =>
                setUnit((Array.from(keys)[0] as ProgramUnit) ?? unit)
              }
            >
              {PROGRAM_UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {t(PROGRAM_UNIT_LABELS[u])}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Semanas según')}
              selectedKeys={[dateBasis]}
              onSelectionChange={(keys) =>
                setDateBasis(
                  (Array.from(keys)[0] as ProgramDateBasis) ?? dateBasis
                )
              }
            >
              <SelectItem key='eta' value='eta'>
                {t('Arribo (ETA)')}
              </SelectItem>
              <SelectItem key='etd' value='etd'>
                {t('Despacho (ETD)')}
              </SelectItem>
            </Select>
          </div>

          {/* Solo las equivalencias de las unidades en juego (la elegida y, si
              se cambia, la anterior). */}
          {(needsCeqKg || needsShipmentKg) && (
            <div className='grid grid-cols-2 gap-3'>
              {needsCeqKg && (
                <Input
                  label={t('Kilos por caja equivalente')}
                  inputMode='decimal'
                  value={kgPerCeq}
                  onValueChange={setKgPerCeq}
                  endContent={
                    <span className='text-xs text-default-400'>kg</span>
                  }
                  isInvalid={!ceq}
                />
              )}
              {needsShipmentKg && (
                <Input
                  label={t('Kilos por embarque')}
                  inputMode='decimal'
                  value={kgPerShipment}
                  onValueChange={setKgPerShipment}
                  endContent={
                    <span className='text-xs text-default-400'>kg</span>
                  }
                  isInvalid={shipmentKgInvalid}
                  description={t('Un aéreo cuenta como un embarque.')}
                />
              )}
            </div>
          )}

          {(unitChanged || basisChanged) && (
            <Alert variant='warning' showIcon>
              <p>
                {t(
                  'Se guarda una versión con el programa como está ahora, para seguir comparando contra él.'
                )}
                {unitChanged &&
                  ` ${t('Lo cargado pasa a {{unit}} con las equivalencias de arriba.', { unit: unitLabel(unit) })}`}
              </p>
              {preview.length > 0 && (
                <table className='mt-2 w-full text-xs font-normal'>
                  <tbody>
                    {preview.map((row) => (
                      <tr key={row.label}>
                        <td className='py-1'>{row.label}</td>
                        <td className='py-1 text-right'>
                          {formatProgramQuantity(row.quantity, program!.unit)}{' '}
                          {PROGRAM_UNIT_SHORT[program!.unit]}
                        </td>
                        <td className='px-2 py-1 text-center'>→</td>
                        <td className='py-1 text-right font-semibold'>
                          {row.converted == null
                            ? '–'
                            : `${formatProgramQuantity(row.converted, unit)} ${PROGRAM_UNIT_SHORT[unit]}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={onClose}>
            {t('Cancelar')}
          </Button>
          <Button
            color='primary'
            onPress={handleSave}
            isLoading={saving}
            isDisabled={invalid}
          >
            {program ? t('Guardar') : t('Crear programa')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
